import {
  ApolloClient,
  ApolloError,
  ApolloProvider,
  from,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";
import { uniq } from "lodash";
import React, { useEffect, useMemo } from "react";

import { CachePersistor, LocalStorageWrapper } from "apollo3-cache-persist";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ErrorReport, reportError } from "lib/Datadog";
import isNotNullish from "lib/isNotNullish";
import { getNodesFromConnection } from "lib/Relay";
import { Path } from "lib/utilityTypes";
import { AccountTier, ProductGridQuery } from "operations";
import { SizeRun } from "queries/SizeMappings";

type ImagesConnection = Path<
  ProductGridQuery,
  ["assortment", "skus", "edges", "color", "images"]
>;

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        productDetail: (existing, { args, toReference, readField }) => {
          const styleRef = toReference({ __typename: "Style", id: args?.id });
          const imagesConnection = readField("images", styleRef);

          const images = getNodesFromConnection(
            imagesConnection as ImagesConnection
          );

          const product = { images };

          return existing ?? product;
        },
        sizeRuns: {
          merge(existing, incoming: SizeRun[]) {
            return [...incoming].sort((a, b) =>
              a.code && b.code
                ? a.code.localeCompare(b.code, undefined, { numeric: true })
                : 0
            );
          },
        },
      },
    },
    Assortment: {
      fields: {
        financialOverview: {
          merge: true,
        },
      },
    },
    Style: {
      fields: {
        details: {
          merge: true,
        },
      },
    },
    ColorSwatch: {
      merge: true,
    },
    AssortmentUI__ColorUnitQuantity: {
      keyFields: ["colorId"],
    },
    AssortmentUI__SkuUnitQuantity: {
      keyFields: ["sku", ["id"]],
    },
    Account: {
      fields: {
        tier: () => AccountTier.Pro,
      },
    },
  },
});

const defaultLink = from([
  onError(({ forward, graphQLErrors, networkError, operation, response }) => {
    forward(operation).forEach((result) => {
      const { operationName, variables, getContext } = operation;
      reportError({
        message: `[${operationName}] An error has occurred during operation.`,
        context: { variables, ...getContext() },
        errors: [
          ...(graphQLErrors ?? []),
          ...(response?.errors ?? []),
          ...(result.errors ?? []),
          networkError,
        ].filter(isNotNullish),
      });
    });
  }),
  createUploadLink({
    uri: process.env.REACT_APP_GRAPHQL_URL,
    credentials: "include",
  }),
]);

const retryUnavailable = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        // 14 is the code for UNAVAILABLE errors
        if (err.extensions.exception?.code === 14) {
          // log and retry the operation
          console.log(err);
          return forward(operation);
        }
      }
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`);
    }
  }
);

export const apolloClient = new ApolloClient({
  assumeImmutableResults: true,
  cache,
  link: from([retryUnavailable, defaultLink]),
});

export const Provider: React.FC = ({ children }) => {
  const client = apolloClient;
  const { enablePersistentCache } = useFlags();
  const cachePersistor = useMemo(
    () =>
      new CachePersistor({
        cache,
        storage: new LocalStorageWrapper(sessionStorage),
        debug: process.env.NODE_ENV !== "production",
        maxSize: 4000000, //4 MB. Chrome only allows for 5MB of local storage. We will use 4 to leave room for other processes
        trigger: enablePersistentCache ? "write" : false,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (enablePersistentCache === true) {
      cachePersistor.restore().catch(console.error);
    } else {
      cachePersistor.remove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePersistentCache]);

  return <ApolloProvider client={client} children={children} />;
};

// see [the apollo docs](https://www.apollographql.com/docs/react/api/link/apollo-link-error#error-categorization) for more info about ApolloError
export const extractErrorReportFromApolloError = (
  error: ApolloError,
  message?: string
): ErrorReport => ({
  message: message ?? error.message,
  context: { ...error.extraInfo },
  errors: [
    ...(error.graphQLErrors ?? []),
    ...(error.clientErrors ?? []),
    error.cause,
    error.networkError,
  ].filter(isNotNullish) as (Error | ErrorReport)[],
});

export const extractMessagesFromApolloError = (error: ApolloError) => {
  const messages: string[] = [...error.graphQLErrors.map((e) => e.message)];
  if (error.networkError) {
    if ("result" in error.networkError) {
      error.networkError.result.errors?.forEach((e: any) => {
        if ("message" in e) {
          messages.push(e.message);
        }
      });
    } else if ("bodyText" in error.networkError) {
      messages.push(error.networkError.bodyText);
    } else {
      messages.push(error.networkError.message);
    }
  }
  return messages.length > 0 ? uniq(messages) : [error.message];
};
